import { CurrencyPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TagComponent } from '@catch/general-ui';
import { UnixToDatePipe } from '@catch/pipes';
import { ConstantsService, LanguageService } from '@catch/services';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe } from '@jsverse/transloco';
import countries from 'i18n-iso-countries';
import { TargetBlankDirective } from '../../../directives/target-blank.directive';
import { EmploymentType } from '../../../enums/employment-type.enum';
import { SalaryInterval } from '../../../enums/salary-interval.enum';
import { SearchRegionType } from '../../../enums/search-region-type.enum';
import { WorkingModel } from '../../../enums/working-model.enum';
import { WorkingTime } from '../../../enums/working-time.enum';
import { Facility } from '../../../interfaces/facility';
import { ColorService } from '../../../services/color.service';
import { FontawesomeService } from '../../../services/fontawesome.service';

@Component({
  selector: 'app-header-tags',
  templateUrl: './header-tags.component.html',
  styleUrls: ['./header-tags.component.scss'],
  imports: [
    TagComponent,
    FaIconComponent,
    TranslocoPipe,
    UnixToDatePipe,
    TargetBlankDirective,
    CurrencyPipe,
  ],
})
export class HeaderTagsComponent {
  startDate = input<number | null>(null);
  employmentType = input<EmploymentType | null>(null);
  workingTime = input<WorkingTime | null>(null);
  locations = input<Facility[]>([]);
  website = input<string | null>(null);
  workingModel = input<WorkingModel | null>(null);
  searchRegionAddress = input<Facility | null>(null);
  showSalaryRangeOnJobboard = input(false);
  salaryCurrency = input<string | null>(null);
  salaryMin = input<number | null>(null);
  salaryMax = input<number | null>(null);
  /** Denormalized single salary when min/max are not set separately */
  fallbackSalary = input<number | null>(null);
  salaryInterval = input<SalaryInterval | null>(null);
  public filteredLocations = computed<Facility[]>(() => {
    const uniqueCities = new Set(this.locations().map((location) => location.city));
    return Array.from(uniqueCities).map((city) => ({ city } as Facility));
  });

  public facilityString = computed(() => {
    const searchRegionAddress = this.searchRegionAddress();
    const filteredLocations = this.filteredLocations();
    switch (this.workingModel()) {
      case WorkingModel.ON_SITE:
        return filteredLocations.map((location) => location.city).join(', ');
      case WorkingModel.HYBRID:
      case WorkingModel.FIELD_SERVICE:
        return `(${filteredLocations.map((location) => location.city).join(', ')})`;
      case WorkingModel.REMOTE: {
        const label = this.remoteSearchRegionLabel(searchRegionAddress);
        return label ? `(${label})` : '';
      }
      default:
        return '';
    }
  });
  public timestampInPast = computed(() => {
    const startDate = this.startDate();
    if (!startDate) {
      return false;
    }

    return startDate < Date.now() / 1000;
  });

  displayJobboardSalaryMin = computed(
    () => this.salaryMin() ?? this.fallbackSalary(),
  );
  displayJobboardSalaryMax = computed(
    () => this.salaryMax() ?? this.fallbackSalary(),
  );
  public showJobboardSalaryTag = computed(() => {
    if (!this.showSalaryRangeOnJobboard()) {
      return false;
    }
    const min = this.displayJobboardSalaryMin();
    const max = this.displayJobboardSalaryMax();
    return min != null && max != null;
  });

  public WorkingModel = WorkingModel;

  public colorService = inject(ColorService);
  public fontawesomeService = inject(FontawesomeService);
  public constantsService = inject(ConstantsService);
  private languageService = inject(LanguageService);

  private remoteSearchRegionLabel(address: Facility | null | undefined): string {
    if (!address) {
      return '';
    }
    const lang = this.languageService.currentLanguage.value.split('-')[0];
    const countryCode = address.countryCode ?? address.country;

    switch (address.searchRegionType) {
      case SearchRegionType.CITY:
        return address.city ?? '';
      case SearchRegionType.COUNTRY:
        return countryCode ? (countries.getName(countryCode, lang) ?? countryCode) : '';
      case SearchRegionType.STATE:
        return address.state ?? '';
      default:
        if (address.city) {
          return address.city;
        }
        if (address.state) {
          return address.state;
        }
        if (countryCode) {
          return countries.getName(countryCode, lang) ?? countryCode;
        }
        return '';
    }
  }
}
